import { Directive, ElementRef, HostListener, forwardRef, inject, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[ldPhoneMask]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PhoneMaskDirective), multi: true },
  ],
})
export class PhoneMaskDirective implements ControlValueAccessor, OnInit {
  private readonly el = inject(ElementRef<HTMLInputElement>);

  private onChange: ((value: string) => void) | null = null;
  private onTouched: (() => void) | null = null;

  ngOnInit(): void {
    const input = this.el.nativeElement;
    if (input.value) {
      input.value = this.formatPhoneNumber(input.value);
    }
  }

  writeValue(value: string | null): void {
    const digits = (value ?? '').replace(/\D/g, '').substring(0, 10);
    this.el.nativeElement.value = this.formatPhoneNumber(digits);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').substring(0, 10);
    const formatted = this.formatPhoneNumber(input.value);
    input.value = formatted;

    this.onChange?.(digits);
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const input = this.el.nativeElement;
    const digits = pastedText.replace(/\D/g, '').substring(0, 10);
    const formatted = this.formatPhoneNumber(pastedText);
    input.value = formatted;

    this.onChange?.(digits);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched?.();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  private formatPhoneNumber(value: string): string {
    const digits = value.replace(/\D/g, '');

    const limited = digits.substring(0, 10);

    if (limited.length === 0) {
      return '';
    } else if (limited.length <= 3) {
      return `(${limited}`;
    } else if (limited.length <= 6) {
      return `(${limited.substring(0, 3)}) ${limited.substring(3)}`;
    } else {
      return `(${limited.substring(0, 3)}) ${limited.substring(3, 6)}-${limited.substring(6)}`;
    }
  }
}
